import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Loader2, Search } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import type { Projeto, ProjetoEtapa, Profile, Tarefa } from '../../types';
import { calcularProgresso, type Progresso, type TarefaProgresso } from '../../src/lib/projetos/progresso';
import { calcularEquipe, type MembroEquipe, type TarefaEquipe } from '../../src/lib/projetos/equipe';
import { TODAS_ETAPAS } from '../../src/lib/projetos/etapas';
import { calcularResumoExecucao } from '../../src/lib/projetos/resumoExecucao';
import { montarAtividadeGeral, type ItemAtividadeGeral, type ProjetoComEntradas } from '../../src/lib/projetos/atividadeGeral';
import type { ComentarioProjetoBruto, ComentarioTarefaBruto, EventoStatusBruto } from '../../src/lib/projetos/timeline';
import ProjetoCard from './ProjetoCard';
import ProjetoFormModal from './ProjetoFormModal';
import ResumoExecucaoFaixa from './ResumoExecucaoFaixa';
import AtividadeRecentePanel from './AtividadeRecentePanel';

type TarefaResumo = Pick<Tarefa, 'id' | 'projeto_id' | 'parent_id' | 'responsavel_id' | 'solicitacao_status' | 'status' | 'vencimento' | 'titulo' | 'data_conclusao'>;

// titulo entra pro painel de atividade (rótulo do item), data_conclusao pro KPI de 7 dias.
const COLUNAS = 'id, projeto_id, parent_id, responsavel_id, solicitacao_status, status, vencimento, titulo, data_conclusao';
const PROGRESSO_VAZIO: Progresso = { total: 0, concluidas: 0, pct: 0, atrasadas: 0 };

const ProjetosView: React.FC = () => {
  const { profile } = useAuth();
  // Só adm abre projeto (espelha a policy projetos_insert). Gerente abria projeto achando que
  // era solicitação — pedido do dia a dia vai pela Central de Solicitações.
  const podeCriar = profile?.role === 'adm';

  const [projetos, setProjetos] = useState<Projeto[]>([]);
  const [tarefas, setTarefas] = useState<TarefaResumo[]>([]);
  const [users, setUsers] = useState<Profile[]>([]);
  const [atividade, setAtividade] = useState<ItemAtividadeGeral[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [etapaFiltro, setEtapaFiltro] = useState<ProjetoEtapa | ''>('');
  const [tagFiltro, setTagFiltro] = useState('');
  const [modalAberto, setModalAberto] = useState(false);

  useEffect(() => {
    (async () => {
      setCarregando(true);
      const [pRes, maesRes, uRes] = await Promise.all([
        supabase.from('projetos')
          .select('*, responsavel:profiles!projetos_responsavel_id_fkey(id, full_name, avatar_url)')
          .order('created_at', { ascending: false }),
        // Só o necessário pro progresso do card — a tela de detalhe é que carrega a tarefa inteira.
        supabase.from('tarefas').select(COLUNAS).not('projeto_id', 'is', null),
        // Sem consumidores: eles passam de 1.300 e o Supabase devolve no máximo 1.000 linhas, então
        // quem ficava depois do corte (o Victor, por exemplo) aparecia como "Alguém" na atividade.
        supabase.from('profiles').select('*').neq('role', 'consumidor'),
      ]);

      // Subtarefa não carrega projeto_id (pertence ao projeto pela mãe), mas conta no progresso.
      // Sem buscá-la aqui, o card mostraria um número e a página de detalhe outro para o mesmo
      // projeto — e o número menor sempre parece "mais atrasado" do que a realidade.
      const maes = (maesRes.data || []) as TarefaResumo[];
      const idsMae = maes.map(t => t.id);
      const { data: filhas } = idsMae.length
        ? await supabase.from('tarefas').select(COLUNAS).in('parent_id', idsMae)
        : { data: [] };
      const projetoDaMae = new Map(maes.map(t => [String(t.id), t.projeto_id]));
      const filhasComProjeto = ((filhas || []) as TarefaResumo[])
        .map(f => ({ ...f, projeto_id: projetoDaMae.get(String(f.parent_id)) ?? null }));
      const todasTarefas = [...maes, ...filhasComProjeto];

      const projetosCarregados = (pRes.data || []) as Projeto[];
      if (pRes.data) setProjetos(pRes.data as Projeto[]);
      setTarefas(todasTarefas);
      if (uRes.data) setUsers(uRes.data as Profile[]);

      // Painel de Atividade Recente: comentários e mudanças de status de TODOS os projetos,
      // escopados pelos ids já carregados acima — nenhuma consulta nova de projeto ou tarefa.
      const idsProjeto = projetosCarregados.map(p => p.id);
      const idsTarefaTodas = todasTarefas.map(t => t.id);
      const [muralRes, comentTarefaRes, eventosRes] = await Promise.all([
        idsProjeto.length
          ? supabase.from('comentarios_projetos').select('*').in('projeto_id', idsProjeto)
          : Promise.resolve({ data: [] as (ComentarioProjetoBruto & { projeto_id: number })[] }),
        idsTarefaTodas.length
          ? supabase.from('comentarios_tarefas').select('*').in('tarefa_id', idsTarefaTodas)
          : Promise.resolve({ data: [] as ComentarioTarefaBruto[] }),
        idsTarefaTodas.length
          ? supabase.from('tarefa_status_events').select('*').in('tarefa_id', idsTarefaTodas)
          : Promise.resolve({ data: [] as EventoStatusBruto[] }),
      ]);

      // comentarios_tarefas e tarefa_status_events não carregam projeto_id (a tabela não tem essa
      // coluna) — o agrupamento usa o mesmo mapa tarefa_id -> projeto_id que resolve as subtarefas.
      const tarefaParaProjeto = new Map(todasTarefas.map(t => [String(t.id), t.projeto_id]));
      const tarefaParaTitulo = new Map(todasTarefas.map(t => [String(t.id), t.titulo]));

      const muralPorProjeto = new Map<number, ComentarioProjetoBruto[]>();
      ((muralRes.data || []) as (ComentarioProjetoBruto & { projeto_id: number })[]).forEach(c => {
        const lista = muralPorProjeto.get(c.projeto_id) || [];
        lista.push(c);
        muralPorProjeto.set(c.projeto_id, lista);
      });
      const comentTarefaPorProjeto = new Map<number, ComentarioTarefaBruto[]>();
      ((comentTarefaRes.data || []) as ComentarioTarefaBruto[]).forEach(c => {
        const projetoId = tarefaParaProjeto.get(String(c.tarefa_id));
        if (projetoId == null) return;
        const lista = comentTarefaPorProjeto.get(projetoId) || [];
        lista.push(c);
        comentTarefaPorProjeto.set(projetoId, lista);
      });
      const eventosPorProjeto = new Map<number, EventoStatusBruto[]>();
      ((eventosRes.data || []) as EventoStatusBruto[]).forEach(e => {
        const projetoId = tarefaParaProjeto.get(String(e.tarefa_id));
        if (projetoId == null) return;
        const lista = eventosPorProjeto.get(projetoId) || [];
        lista.push(e);
        eventosPorProjeto.set(projetoId, lista);
      });

      const entradasPorProjeto: ProjetoComEntradas[] = projetosCarregados.map(p => ({
        projetoId: p.id,
        projetoNome: p.nome,
        entradas: {
          comentariosProjeto: muralPorProjeto.get(p.id) || [],
          comentariosTarefas: comentTarefaPorProjeto.get(p.id) || [],
          eventosStatus: eventosPorProjeto.get(p.id) || [],
          titulosDeTarefa: tarefaParaTitulo,
        },
      }));
      // Corta em 20: com o volume de hoje (poucos projetos, baixa atividade) não se justifica
      // paginar. Se o portfólio crescer bastante, aqui é o primeiro ponto a revisitar.
      setAtividade(montarAtividadeGeral(entradasPorProjeto).slice(0, 20));

      setCarregando(false);
    })();
  }, []);

  /** Progresso por projeto, calculado uma vez para todos os cards. */
  const progressoPorProjeto = useMemo(() => {
    const mapa = new Map<number, Progresso>();
    const porProjeto = new Map<number, TarefaProgresso[]>();
    tarefas.forEach(t => {
      if (t.projeto_id == null) return;
      const lista = porProjeto.get(t.projeto_id) || [];
      lista.push(t as TarefaProgresso);
      porProjeto.set(t.projeto_id, lista);
    });
    projetos.forEach(p => mapa.set(p.id, calcularProgresso(porProjeto.get(p.id) || [])));
    return mapa;
  }, [projetos, tarefas]);

  /** Equipe por projeto: o dono declarado do projeto mais quem tem tarefa nele. */
  const equipePorProjeto = useMemo(() => {
    const porProjeto = new Map<number, TarefaEquipe[]>();
    tarefas.forEach(t => {
      if (t.projeto_id == null) return;
      const lista = porProjeto.get(t.projeto_id) || [];
      lista.push(t as TarefaEquipe);
      porProjeto.set(t.projeto_id, lista);
    });
    const mapa = new Map<number, MembroEquipe[]>();
    projetos.forEach(p => mapa.set(p.id, calcularEquipe({
      gestorId: p.responsavel_id,
      tarefas: porProjeto.get(p.id) || [],
    })));
    return mapa;
  }, [projetos, tarefas]);

  /** KPIs do portfólio — puro, recalcula só quando projetos/tarefas mudam. */
  const resumo = useMemo(
    () => calcularResumoExecucao(projetos, tarefas, new Date()),
    [projetos, tarefas],
  );

  const todasTags = useMemo(
    () => Array.from(new Set(projetos.flatMap(p => p.tags))).sort(),
    [projetos],
  );

  const visiveis = projetos.filter(p => {
    if (etapaFiltro && p.etapa !== etapaFiltro) return false;
    if (tagFiltro && !p.tags.includes(tagFiltro)) return false;
    if (busca && !p.nome.toLowerCase().includes(busca.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Projetos</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Iniciativas que agrupam solicitações da Central de Solicitações.
          </p>
        </div>
        {podeCriar && (
          <button onClick={() => setModalAberto(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg">
            <Plus size={16} /> Novo Projeto
          </button>
        )}
      </div>

      {!carregando && <ResumoExecucaoFaixa resumo={resumo} />}

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar projeto..."
            className="pl-8 pr-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-slate-200" />
        </div>
        <select value={etapaFiltro} onChange={e => setEtapaFiltro(e.target.value as ProjetoEtapa | '')}
          className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-slate-200">
          <option value="">Todas as etapas</option>
          {TODAS_ETAPAS.map(e => <option key={e.valor} value={e.valor}>{e.rotulo}</option>)}
        </select>
        <select value={tagFiltro} onChange={e => setTagFiltro(e.target.value)}
          className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-slate-200">
          <option value="">Todas as tags</option>
          {todasTags.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          {carregando ? (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
              <Loader2 size={16} className="animate-spin" /> Carregando projetos...
            </div>
          ) : visiveis.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <p className="font-medium">Nenhum projeto por aqui.</p>
              <p className="text-sm">
                {podeCriar ? 'Crie o primeiro projeto e vincule as solicitações a ele.' : 'Projetos são abertos pela administração. Para pedir algo, use a Central de Solicitações.'}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {visiveis.map(p => (
                <ProjetoCard key={p.id} projeto={p}
                  progresso={progressoPorProjeto.get(p.id) || PROGRESSO_VAZIO}
                  equipe={equipePorProjeto.get(p.id) || []}
                  users={users} />
              ))}
            </div>
          )}
        </div>

        <div className="xl:col-span-1">
          <AtividadeRecentePanel itens={atividade} users={users} carregando={carregando} />
        </div>
      </div>

      {modalAberto && (
        <ProjetoFormModal
          projeto={null}
          users={users}
          onClose={() => setModalAberto(false)}
          onSaved={novo => setProjetos(prev => [novo, ...prev])}
        />
      )}
    </div>
  );
};

export default ProjetosView;
